import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FeatureFlags {
  advancedProductSearch: boolean;
  limitSearchByVin: boolean;
  replacementProducts: boolean;
  aiPartRecognition: boolean;
  digitalAssets: boolean;
  messageBoard: boolean;
  sharedClaims: boolean;
  demoMode: boolean;
  debugMode: boolean;
  aiFeatures: boolean;
  inventoryTransfers: boolean;
  mockMode: boolean;
  allowDealerSpiffClaims: boolean;
}

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  toggleFlag: (flag: keyof FeatureFlags) => void;
  isFeatureEnabled: (flag: keyof FeatureFlags) => boolean;
  isLoading: boolean;
}

const defaultFlags: FeatureFlags = {
  advancedProductSearch: false,
  limitSearchByVin: false,
  replacementProducts: false,
  aiPartRecognition: false,
  digitalAssets: false,
  messageBoard: false,
  sharedClaims: false,
  demoMode: false,
  debugMode: false,
  aiFeatures: true,
  inventoryTransfers: true,
  mockMode: false,
  allowDealerSpiffClaims: true,
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch feature flags from database on mount
  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-feature-flags');
        
        if (error) {
          console.error('Error fetching feature flags:', error);
          return;
        }

        if (data?.flags) {
          setFlags(data.flags);
        }
      } catch (error) {
        console.error('Error fetching feature flags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlags();
  }, []);

  const toggleFlag = async (flag: keyof FeatureFlags) => {
    const newValue = !flags[flag];
    
    // Optimistically update UI
    setFlags(prev => ({
      ...prev,
      [flag]: newValue
    }));

    try {
      const { error } = await supabase.functions.invoke('update-feature-flag', {
        body: { flagName: flag, enabled: newValue }
      });

      if (error) {
        console.error('Error updating feature flag:', error);
        // Revert on error
        setFlags(prev => ({
          ...prev,
          [flag]: !newValue
        }));
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
      // Revert on error
      setFlags(prev => ({
        ...prev,
        [flag]: !newValue
      }));
    }
  };

  const isFeatureEnabled = (flag: keyof FeatureFlags) => {
    return flags[flag];
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, toggleFlag, isFeatureEnabled, isLoading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};
